// src/api/products.js
// 商品一覧とメーカー一覧を、APIのデータベースから読み込む。
// APIから取れなかったときは、ビルド時に同梱したデータ（予備）を使う。
import { useEffect, useState } from "react";
import { BRAND_MAP } from "../data/brandMap";

const API_MODE = process.env.REACT_APP_API_MODE ?? "real";
const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:3001";

// この時間を過ぎても返事がなければ、予備のデータに切り替える
const TIMEOUT_MS = 8000;

async function fetchJson(path) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const res = await fetch(`${API_BASE}${path}`, { signal: controller.signal });
    if (!res.ok) throw new Error(`${path} returned ${res.status}`);
    return await res.json();
  } finally {
    clearTimeout(timer);
  }
}

// 同梱の予備データ。必要になったときだけ読み込む（普段はダウンロードされない）
async function loadBundledProducts() {
  const mod = await import("../data/products.generated.json");
  return mod.default;
}

async function fetchProducts() {
  if (API_MODE === "mock") return loadBundledProducts();

  try {
    const products = await fetchJson("/products");
    if (!Array.isArray(products) || products.length === 0) {
      throw new Error("/products returned no products");
    }
    return products;
  } catch (e) {
    console.error("商品一覧をAPIから取得できなかったため、同梱のデータを使います:", e);
    return loadBundledProducts();
  }
}

// { ipro: "i-PRO", ... } の形（src/data/brandMap.js と同じ）で返す
async function fetchBrandMap() {
  if (API_MODE === "mock") return BRAND_MAP;

  try {
    const makers = await fetchJson("/makers");
    if (!Array.isArray(makers) || makers.length === 0) {
      throw new Error("/makers returned no makers");
    }
    return Object.fromEntries(makers.map((m) => [m.slug, m.makerName]));
  } catch (e) {
    console.error("メーカー一覧をAPIから取得できなかったため、同梱のデータを使います:", e);
    return BRAND_MAP;
  }
}

// 1回読み込んだら、ページを移動しても使い回す
let productsPromise = null;
let brandMapPromise = null;

export function loadProducts() {
  if (!productsPromise) {
    productsPromise = fetchProducts().catch((e) => {
      productsPromise = null; // 次の機会にもう一度試せるようにする
      throw e;
    });
  }
  return productsPromise;
}

export function loadBrandMap() {
  if (!brandMapPromise) brandMapPromise = fetchBrandMap();
  return brandMapPromise;
}

function useLoaded(load, initial) {
  const [state, setState] = useState({ data: initial, ready: false });

  useEffect(() => {
    let active = true;
    load()
      .then((data) => {
        if (active) setState({ data, ready: true });
      })
      .catch(console.error);
    return () => {
      active = false;
    };
  }, [load]);

  return state;
}

// 画面から使う形。ready が true になるまでは、products は空の配列
export function useProducts() {
  const { data, ready } = useLoaded(loadProducts, []);
  return { products: data, ready };
}

export function useBrandMap() {
  const { data, ready } = useLoaded(loadBrandMap, {});
  return { brandMap: data, ready };
}
